import { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import AttendancePaymentLinkInfo from '@components/attendance-payment-link-info/AttendancePaymentLinkInfo';
import CustomTable from '@components/custom-table/CustomTable';
import { CustomTableRowData } from '@components/custom-table/types';
import { Input, Modal, PaymentCollection } from '@components/index';
import MonthCard from '@components/payment-collection/components/MonthCard';
import { isFirstDayFrequencyEquivalent } from '@components/payment-collection/helper';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import {
  addFromMembersTableColumn,
  PaymentCollectionType
} from '@features/group-management/constants';
import { getAdvancedByTermFrequencyPlaceholder } from '@features/group-management/helper';
import { AddFromMembersListItem } from '@features/group-management/types';
import SelectDateInput from '@features/member-management/components/SelectDateInput';
import { MAX_QUICK_COLLECT_MEMBER_COUNT } from '@features/quick-collect/constants';
import ModalV2 from '@layouts/modal/Modal.v2';
import {
  FeeConfigurationType,
  PaymentCollectionArg,
  PaymentCollectionComponentType
} from '@types';
import { formatISODate } from '@utils/date';

import DueAmountField from './DueAmountField';
import DueDateSelector from './DueDateSelector';

const getMonthDayLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return '1st day of the month';
  if (activationDate === 'last_day') return 'Last day of the month';
  if (activationDate && !Number.isNaN(Number(activationDate))) {
    const n = Number(activationDate);
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} day of the month`;
  }
  return '1st day of the month';
};

interface MembeListTableProps {
  memberDetails: AddFromMembersListItem[];
  selectedRows: string[];
  page: number;
  totalCount: number;
  handleStartDateChange?: (memberId: string, dueDate?: Date) => void;
  setSelectedRows: (rows: string[]) => void;
  setPage: (count: number) => void;
  isFeeCategoryEnabled?: boolean;
  feeCategoryData?: FeeConfigurationType[];
  onFeeCategoryAmountSave: (
    params: FeeConfigurationType[],
    memberIndex: number
  ) => void;
  onAmountChange: (dueAmount: string, memberIndex: number) => void;
  isQuickCollect?: boolean;
  groupPaymentCollectionType?:
    | PaymentCollectionType
    | PaymentCollectionComponentType;
  groupDueDate?: string;
  groupFrequency?: number;
  isImmediatePayment: boolean;
  isCustomTerm: boolean;
  showFrequencySelector?: boolean;
  handleFrequencyChange?: (
    memberId: string,
    data: PaymentCollectionArg
  ) => void;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  handleSessionsChange?: (value: string, memberIndex: number) => void;
  defaultTermLabel?: string;
}

const MemberListTable: FC<MembeListTableProps> = ({
  isFeeCategoryEnabled = false,
  feeCategoryData = [],
  memberDetails,
  selectedRows,
  page,
  totalCount,
  onAmountChange,
  handleStartDateChange,
  onFeeCategoryAmountSave,
  setSelectedRows,
  setPage,
  isQuickCollect = false,
  groupPaymentCollectionType,
  isImmediatePayment,
  isCustomTerm,
  showFrequencySelector,
  groupDueDate,
  groupFrequency,
  handleFrequencyChange,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  handleSessionsChange,
  defaultTermLabel
}) => {
  const disabledRowIds: string[] = [''];
  const { orgConfig } = useSelector((state: any) => state.user);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const [billingMemberId, setBillingMemberId] = useState<string>('');
  const [tempActivationDate, setTempActivationDate] =
    useState<string>('first_day');

  const onDueDateChange = (memberId: string, data: PaymentCollectionArg) => {
    handleFrequencyChange?.(memberId, { ...data });
    setIsCalendarVisible(false);
  };

  const handleOpenBillingModal = (memberId: string) => {
    setBillingMemberId(memberId);
    const member = memberDetails.find((m) => m.id === memberId);
    setTempActivationDate(member?.dueDate || groupDueDate || 'first_day');
    setIsBillingModalOpen(true);
  };

  const handleBillingModalApply = () => {
    handleFrequencyChange?.(billingMemberId, {
      date: tempActivationDate,
      interval: '1',
      type: PaymentCollectionType.MONTHLY
    });
    setIsBillingModalOpen(false);
    setBillingMemberId('');
  };

  const handleBillingModalCancel = () => {
    setTempActivationDate('');
    setIsBillingModalOpen(false);
    setBillingMemberId('');
  };

  const getUnselectableFrequency = () => {
    if (isCustomTerm)
      return getAdvancedByTermFrequencyPlaceholder(defaultTermLabel);
    return DEFAULT_PLACEHOLDER;
  };

  const modifiedTableData = memberDetails?.map(
    (row: AddFromMembersListItem, index) => {
      if (row.isAlreadyPresent) disabledRowIds.push(row.id);

      if (isAttendanceBasedFee) {
        return {
          id: row.id,
          name: (
            <p className="table w-full table-fixed break-words">
              {row.name || DEFAULT_PLACEHOLDER}
            </p>
          ),
          phoneNumber:
            row.mobile || row.primary_mobile_number
              ? `+${row.countryCode || row.primary_country_code} ${
                  row.mobile || row.primary_mobile_number
                }`
              : DEFAULT_PLACEHOLDER,
          ...(!row.isAlreadyPresent
            ? {
                dueAmount: (
                  <DueAmountField
                    rowIndex={index}
                    onFeeCategorySave={(params: FeeConfigurationType[]) =>
                      onFeeCategoryAmountSave(params, index)
                    }
                    isFeeCategoryEnabled={isFeeCategoryEnabled}
                    row={row}
                    feeCategoryData={feeCategoryData}
                    onAmountChange={onAmountChange}
                  />
                ),

                billingPeriod: isSessionBased ? (
                  <Input
                    type="number"
                    value={row.sessionsPerCycle ?? '0'}
                    onChange={(e) => {
                      const num = e.target.value
                        ? Number(e.target.value)
                        : undefined;
                      if (num && num > 9999) return;
                      handleSessionsChange?.(e.target.value, index);
                    }}
                    placeholder="0"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    suffixText={
                      row.sessionsPerCycle && row.sessionsPerCycle > 1
                        ? 'Classes'
                        : 'Class'
                    }
                  />
                ) : (
                  <Input
                    leftIconName="calendar"
                    placeholder="Select billing period"
                    value={getMonthDayLabel(
                      row.dueDate || groupDueDate || 'first_day'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenBillingModal(row.id);
                    }}
                    className="w-full"
                  />
                )
              }
            : {
                dueAmount: (
                  <div className="w-full italic text-cofeeNeutral6">
                    Already in group
                  </div>
                ),
                billingPeriod: ''
              })
        };
      }

      return {
        id: row.id,
        name: (
          <p className="table w-full table-fixed break-words">
            {row.name || DEFAULT_PLACEHOLDER}
          </p>
        ),
        phoneNumber:
          row.mobile || row.primary_mobile_number
            ? `+${row.countryCode || row.primary_country_code} ${
                row.mobile || row.primary_mobile_number
              }`
            : DEFAULT_PLACEHOLDER,
        ...(!row.isAlreadyPresent && !isQuickCollect
          ? {
              dueAmount: isCustomTerm ? (
                row.dueAmount
              ) : (
                <DueAmountField
                  rowIndex={index}
                  onFeeCategorySave={(params: FeeConfigurationType[]) =>
                    onFeeCategoryAmountSave(params, index)
                  }
                  isFeeCategoryEnabled={isFeeCategoryEnabled}
                  row={row}
                  feeCategoryData={feeCategoryData}
                  onAmountChange={onAmountChange}
                  customInputStyle="!h-9 !p-2 font-normal"
                />
              ),
              // eslint-disable-next-line no-nested-ternary
              dueDate:
                isImmediatePayment || isCustomTerm
                  ? getUnselectableFrequency()
                  : showFrequencySelector && (
                      <DueDateSelector
                        groupPaymentCollectionType={
                          groupPaymentCollectionType as PaymentCollectionType
                        }
                        setSelectedMemberId={setSelectedMemberId}
                        setIsCalendarVisible={setIsCalendarVisible}
                        row={row}
                      />
                    ),

              startDate: isCustomTerm ? (
                formatISODate({ date: row.startDate || new Date() })
              ) : (
                <SelectDateInput
                  className="max-w-50"
                  modalTitle="Edit Start Date"
                  selectedDate={row.startDate || ''}
                  handleDateSelect={(date) => {
                    handleStartDateChange?.(row.id, date);
                  }}
                  modalTitleStyle="!text-left text-xl font-medium text-content"
                  disabled={
                    row.isAlreadyPresent ||
                    groupPaymentCollectionType === PaymentCollectionType.ONCE
                  }
                  isMinDateRequired
                  inputStyle={
                    row.isAlreadyPresent ||
                    groupPaymentCollectionType === PaymentCollectionType.ONCE
                      ? ''
                      : '!text-cofeeNeutral10'
                  }
                />
              )
            }
          : {
              dueDate: '',
              dueAmount: (
                <div className="w-full italic text-cofeeNeutral6">
                  Already in group
                </div>
              ),
              startDate: ''
            })
      };
    }
  );

  const handleSelect = (row: CustomTableRowData<string>) => {
    if (selectedRows.includes(row.id)) {
      setSelectedRows(selectedRows.filter((id) => id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row.id]);
    }
  };

  const handleSelectAll = (
    selectedIds: string[],
    removedIds: string[] = []
  ) => {
    const newSelectedRows = new Set(selectedRows);

    selectedIds.forEach((id) => newSelectedRows.add(id));
    removedIds.forEach((id) => newSelectedRows.delete(id));

    setSelectedRows(Array.from(newSelectedRows));
  };

  const quickCollectMemberlimit =
    orgConfig?.quick_collect_contact_limit || MAX_QUICK_COLLECT_MEMBER_COUNT;

  const getSelectedMember = () =>
    memberDetails.find((member) => member.id === selectedMemberId);

  const isGroupMonthlyFirstDay =
    groupPaymentCollectionType === PaymentCollectionType.MONTHLY &&
    isFirstDayFrequencyEquivalent(groupDueDate);
  const isGroupTermFirstDay =
    groupPaymentCollectionType === PaymentCollectionType.BY_TERM &&
    isFirstDayFrequencyEquivalent(groupDueDate);

  return (
    <>
      <div className="mt-4 flex h-full min-h-120 flex-col overflow-y-auto">
        <div className="flex-1 overflow-y-auto">
          <CustomTable
            headers={addFromMembersTableColumn(
              isQuickCollect,
              isAttendanceBasedFee
            )}
            data={modifiedTableData}
            handleSelect={handleSelect}
            totalCount={totalCount}
            onPageChange={setPage}
            currentPage={page}
            hasMultiSelect
            handleSelectAll={handleSelectAll}
            selectedRows={selectedRows}
            handleCheckboxSelect={handleSelect}
            maxLimit={quickCollectMemberlimit}
            disabledRowIds={disabledRowIds}
          />
        </div>
      </div>
      {isCalendarVisible && (
        <Modal isOpen={isCalendarVisible}>
          <PaymentCollection
            type={
              // eslint-disable-next-line max-len
              groupPaymentCollectionType as PaymentCollectionComponentType
            }
            isOpen={isCalendarVisible}
            confirmButtonLabel="Confirm"
            confirmButtonStyle="text-primary"
            handleConfirm={({ date, interval, type }: PaymentCollectionArg) =>
              onDueDateChange(selectedMemberId, { date, interval, type })
            }
            cancelButtonLabel="Cancel"
            handleCancel={() => {
              setIsCalendarVisible(false);
              setSelectedMemberId('');
            }}
            selectedDate={getSelectedMember()?.dueDate || groupDueDate}
            selectedtype={
              getSelectedMember()?.paymentCollectionType ||
              PaymentCollectionType.MONTHLY
            }
            selectedFrequency={getSelectedMember()?.interval || groupFrequency}
            source="member"
            isGroupMonthlyFirstDay={isGroupMonthlyFirstDay}
            isGroupTermFirstDay={isGroupTermFirstDay}
          />
        </Modal>
      )}
      {isBillingModalOpen && (
        <ModalV2
          title="Billing period"
          onClose={handleBillingModalCancel}
          primaryButtonLabel="Apply"
          primaryButtonClick={handleBillingModalApply}
          secondaryButtonLabel="Cancel"
          secondaryButtonClick={handleBillingModalCancel}
        >
          <MonthCard
            handleActivationDate={(value: string | Date) =>
              setTempActivationDate(String(value))
            }
            selectedDate={tempActivationDate}
            type={PaymentCollectionType.MONTHLY}
            handleIntervalFrequency={() => {}}
          />
          <AttendancePaymentLinkInfo />
        </ModalV2>
      )}
    </>
  );
};

export default MemberListTable;
